import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/EmptyState";
import { StatusBadge } from "@/components/StatusBadge";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { can, P } from "@/lib/perms";
import { DemandSummary } from "@/components/DemandSummary";
import { SubscriptionCard } from "@/components/SubscriptionCard";
import {
  Package, Handshake, ClipboardList, Clock, CheckCircle2, Truck, Inbox, TrendingUp, TrendingDown, ArrowRight, Plus, Boxes, Wallet, AlertTriangle, RefreshCw, Award, Minus,
} from "lucide-react";

const COUNT_STATUSES = ["New", "Modified", "Accepted", "Delivered", "Missed Delivery", "Declined"];
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function TrendPill({ cur, prev, testid }) {
  const d = cur - prev;
  if (prev === 0) {
    if (d <= 0) return null;
    return (
      <p className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-emerald-600" data-testid={testid}>
        <TrendingUp className="h-3 w-3" /> New this month
      </p>
    );
  }
  if (d === 0) {
    return (
      <p className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-stone-400" data-testid={testid}>
        <Minus className="h-3 w-3" /> No change vs last month
      </p>
    );
  }
  const up = d > 0;
  return (
    <p className={`mt-1 inline-flex items-center gap-1 text-xs font-medium ${up ? "text-emerald-600" : "text-rose-600"}`} data-testid={testid}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />} {up ? "+" : "−"}{Math.abs((d / prev) * 100).toFixed(0)}% vs last month
    </p>
  );
}

export default function Dashboard() {
  const { business, user } = useAuth();
  const canReports = can(user, ...P.reports);
  const canOrders = can(user, ...P.orders);
  const setupStatus = business?.org_setup?.status || "pending";
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [partners, setPartners] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fin, setFin] = useState(null);
  const isVendor = business?.business_type === "vendor";

  const canInv = can(user, "inventory.view", "procurement.create");
  useEffect(() => {
    Promise.all([
      canOrders ? api.get("/orders") : Promise.resolve({ data: { orders: [] } }), api.get("/partners"),
      canInv ? api.get("/inventory").catch(() => ({ data: { inventory: [] } })) : Promise.resolve({ data: { inventory: [] } }),
    ]).then(([o, p, inv]) => {
      setOrders(o.data.orders); setPartners(p.data.partners); setInventory(inv.data.inventory || []);
    }).finally(() => setLoading(false));
  }, [canOrders, canInv]);

  useEffect(() => {
    if (!canReports) { return; }
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const range = (y, m) => ({
      start: `${y}-${pad(m + 1)}-01`,
      end: `${y}-${pad(m + 1)}-${pad(new Date(y, m + 1, 0).getDate())}`,
    });
    const cur = range(now.getFullYear(), now.getMonth());
    const pm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prev = range(pm.getFullYear(), pm.getMonth());
    Promise.all([
      api.get("/reports/summary", { params: cur }),
      api.get("/reports/summary", { params: prev }),
    ]).then(([c, p]) => {
      const e = c.data.earnings || {}; const x = c.data.expenses || {};
      const mapOf = (arr) => Object.fromEntries((arr || []).map((i) => [i.id, i.total]));
      const prevEarn = mapOf((p.data.earnings || {}).by_counterpart);
      const prevExp = mapOf((p.data.expenses || {}).by_counterpart);
      const tb = (e.by_counterpart || [])[0] || null;
      const ts = (x.by_counterpart || [])[0] || null;
      setFin({
        earn: e.total || 0, exp: x.total || 0,
        topBuyer: tb ? { ...tb, prev: prevEarn[tb.id] || 0 } : null,
        topSupplier: ts ? { ...ts, prev: prevExp[ts.id] || 0 } : null,
      });
    }).catch(() => {});
  }, [canReports]);

  const reorder = (it) => navigate("/app/orders", { state: { reorder: {
    partner_business_id: it.last_vendor_id,
    items: [{ product_name: it.product_name, unit: it.unit, quantity: it.suggested_reorder > 0 ? it.suggested_reorder : "" }],
    notes: `Reorder of ${it.product_name} (stock low)`,
  } } });

  if (loading) return <Loader />;

  const lowItems = inventory.filter((it) => (it.quantity || 0) <= (it.threshold ?? 5));

  const counts = COUNT_STATUSES.reduce((a, s) => ({ ...a, [s]: orders.filter((o) => o.status === s).length }), {});
  const incoming = orders.filter((o) => o.direction === "incoming");
  const newIncoming = incoming.filter((o) => o.status === "New");

  const stats = isVendor
    ? [
        { label: "New Requests", value: newIncoming.length, icon: Inbox, color: "#2563EB", to: "/app/orders" },
        { label: "Accepted", value: counts["Accepted"], icon: CheckCircle2, color: "#059669", to: "/app/orders" },
        { label: "Missed", value: counts["Missed Delivery"], icon: Truck, color: "#e11d48", to: "/app/orders" },
        { label: "Delivered", value: counts["Delivered"], icon: CheckCircle2, color: "#16A34A", to: "/app/orders" },
      ]
    : [
        { label: "Total Orders", value: orders.length, icon: ClipboardList, color: "#0D5C3A", to: "/app/orders" },
        { label: "Pending", value: counts["New"] + counts["Modified"], icon: Clock, color: "#D97706", to: "/app/orders" },
        { label: "Delivered", value: counts["Delivered"], icon: CheckCircle2, color: "#16A34A", to: "/app/orders" },
        { label: "Partners", value: partners.length, icon: Handshake, color: "#C85A32", to: "/app/partners" },
      ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-stone-900 sm:text-3xl">
            {isVendor ? "Supply Operations" : "Dashboard"}
          </h1>
          <p className="mt-1 text-stone-500">Welcome back, {user?.name || business?.owner_name}. Here's your overview.</p>
        </div>
        {!isVendor && can(user, "procurement.create") && (
          <Button data-testid="place-order-btn" size="lg" onClick={() => navigate("/app/orders", { state: { placeOrder: true } })} className="bg-[#C85A32] hover:bg-[#a8481f]">
            <Plus className="mr-2 h-5 w-5" /> Place an Order
          </Button>
        )}
      </div>

      <SubscriptionCard />
      {can(user, "orders.view") && <DemandSummary />}

      {user?.role === "owner" && setupStatus !== "completed" && (
        <div data-testid="dashboard-setup-banner" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#0D5C3A]/20 bg-[#F0ECE1] p-5">
          <div>
            <p className="font-heading font-semibold text-[#0D5C3A]">Finish setting up your organisation</p>
            <p className="text-sm text-stone-600">Add departments, roles and your team so everyone gets the right access.</p>
          </div>
          <Button onClick={() => navigate("/app/setup")} data-testid="dashboard-setup-btn" className="bg-[#0D5C3A] hover:bg-[#084229]">{setupStatus === "pending" ? "Start setup" : "Resume setup"}</Button>
        </div>
      )}

      {fin && (fin.earn > 0 || fin.exp > 0) && (() => {
        const net = fin.earn - fin.exp;
        const pos = net > 0;
        const zero = net === 0;
        const tone = zero ? "border-stone-200 bg-stone-50" : pos ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50";
        const amtTone = zero ? "text-stone-700" : pos ? "text-emerald-700" : "text-rose-700";
        const caption = zero ? "· you broke even this month" : pos ? "· you earned more than you spent" : "· you spent more than you earned";
        return (
          <div data-testid="dashboard-net-banner" className={`flex flex-wrap items-center justify-between gap-4 rounded-2xl border p-5 ${tone}`}>
            <div className="flex items-center gap-3">
              {pos ? <TrendingUp className="h-7 w-7 text-emerald-600" /> : zero ? <Wallet className="h-7 w-7 text-stone-500" /> : <TrendingDown className="h-7 w-7 text-rose-600" />}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-stone-400">Net this month {caption}</p>
                <p className={`font-heading text-2xl font-extrabold ${amtTone}`} data-testid="dashboard-net-amount">{zero ? "" : pos ? "+" : "−"}{inr(Math.abs(net))}</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <div><span className="text-stone-400">Earned</span><p className="font-semibold text-[#0D5C3A]">{inr(fin.earn)}</p></div>
              <div><span className="text-stone-400">Spent</span><p className="font-semibold text-[#C85A32]">{inr(fin.exp)}</p></div>
              <button onClick={() => navigate("/app/reports")} className="font-medium text-[#0D5C3A] hover:underline" data-testid="dashboard-net-view-report">View report →</button>
            </div>
          </div>
        );
      })()}

      {lowItems.length > 0 && (
        <div data-testid="dashboard-low-stock" className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <div className="mb-3 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <p className="text-sm font-semibold text-amber-800">{lowItems.length} item{lowItems.length > 1 ? "s" : ""} running low — reorder before you run out</p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {lowItems.slice(0, 6).map((it) => (
              <div key={it.id} data-testid={`dashboard-low-item-${it.id}`} className="flex items-center justify-between gap-2 rounded-xl border border-amber-200 bg-white px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-stone-800">{it.product_name}</p>
                  <p className="text-xs text-rose-600">{Number(it.quantity || 0).toLocaleString("en-IN")} {it.unit} left</p>
                </div>
                {it.last_vendor_id && can(user, "procurement.create") && (
                  <button onClick={() => reorder(it)} data-testid={`dashboard-reorder-${it.id}`}
                    className="flex shrink-0 items-center gap-1 rounded-lg bg-[#0D5C3A] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#084229]">
                    <RefreshCw className="h-3 w-3" /> Reorder
                  </button>
                )}
              </div>
            ))}
          </div>
          {lowItems.length > 6 && <button onClick={() => navigate("/app/inventory")} className="mt-3 text-sm font-medium text-amber-800 hover:underline" data-testid="dashboard-low-stock-viewall">View all low stock →</button>}
        </div>
      )}

      {fin && (fin.topBuyer || fin.topSupplier) && (
        <div data-testid="dashboard-top-partners" className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 text-stone-400"><Award className="h-4 w-4 text-[#0D5C3A]" /><span className="text-xs font-semibold uppercase tracking-wider">Top buyer this month</span></div>
            {fin.topBuyer ? (<><p className="mt-2 font-heading text-lg font-bold text-stone-900" data-testid="dashboard-top-buyer">{fin.topBuyer.name}</p><p className="text-sm font-semibold text-[#0D5C3A]">{inr(fin.topBuyer.total)} earned</p><TrendPill cur={fin.topBuyer.total} prev={fin.topBuyer.prev} testid="dashboard-top-buyer-trend" /></>) : <p className="mt-2 text-sm text-stone-400">No sales yet this month</p>}
          </div>
          <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 text-stone-400"><Award className="h-4 w-4 text-[#C85A32]" /><span className="text-xs font-semibold uppercase tracking-wider">Top supplier this month</span></div>
            {fin.topSupplier ? (<><p className="mt-2 font-heading text-lg font-bold text-stone-900" data-testid="dashboard-top-supplier">{fin.topSupplier.name}</p><p className="text-sm font-semibold text-[#C85A32]">{inr(fin.topSupplier.total)} spent</p><TrendPill cur={fin.topSupplier.total} prev={fin.topSupplier.prev} testid="dashboard-top-supplier-trend" /></>) : <p className="mt-2 text-sm text-stone-400">No purchases yet this month</p>}
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            onClick={() => s.to && navigate(s.to)}
            data-testid={`dashboard-stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="cursor-pointer rounded-2xl border border-[#E5E0D8] bg-white p-5 shadow-sm transition-all hover:border-[#0D5C3A]/40 hover:shadow-md">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-stone-400">{s.label}</span>
              <s.icon className="h-5 w-5" style={{ color: s.color }} />
            </div>
            <p className="mt-3 font-heading text-3xl font-extrabold text-stone-900">{s.value}</p>
          </motion.div>
        ))}
      </div>

      <div className="rounded-2xl border border-[#E5E0D8] bg-white p-4 shadow-sm" data-testid="dashboard-feature-overview">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-stone-400">What you can do here</p>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {(isVendor
            ? [
                { icon: Package, label: "Catalog & pricing", to: "/app/products" },
                { icon: ClipboardList, label: "Fulfil orders", to: "/app/orders" },
                { icon: Wallet, label: "Track earnings", to: "/app/reports" },
                { icon: Handshake, label: "Manage partners", to: "/app/partners" },
              ]
            : [
                { icon: ClipboardList, label: "Place & track orders", to: "/app/orders" },
                { icon: Boxes, label: "Inventory & reorders", to: "/app/inventory" },
                { icon: Wallet, label: "Expenses & insights", to: "/app/reports" },
                { icon: Handshake, label: "Manage partners", to: "/app/partners" },
              ]
          ).filter((q) => ({ "/app/products": can(user, "catalog.view"), "/app/orders": canOrders, "/app/inventory": can(user, "inventory.view"), "/app/reports": canReports, "/app/partners": can(user, "partners.view") })[q.to] ?? true).map((q) => (
            <button key={q.label} onClick={() => navigate(q.to)} data-testid={`overview-${q.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="flex items-center gap-2 rounded-xl border border-[#F0ECE1] bg-[#FAF8F5] px-3 py-2 text-left text-sm text-stone-600 transition-colors hover:border-[#0D5C3A]/30 hover:text-[#0D5C3A]">
              <q.icon className="h-4 w-4 text-[#0D5C3A]" /> {q.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-2xl border border-[#E5E0D8] bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold">{isVendor ? "Incoming Supply Requests" : "Recent Orders"}</h2>
            <button className="inline-flex items-center gap-1 text-sm font-medium text-[#0D5C3A] hover:underline" onClick={() => navigate("/app/orders")} data-testid="dashboard-view-orders">
              View all <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          {(isVendor ? incoming : orders).slice(0, 6).length === 0 ? (
            <p className="py-8 text-center text-sm text-stone-400">No orders yet.</p>
          ) : (
            <div className="divide-y divide-[#F0ECE1]">
              {(isVendor ? incoming : orders).slice(0, 6).map((o) => (
                <button key={o.id} onClick={() => navigate("/app/orders")} className="flex w-full items-center justify-between py-3 text-left hover:bg-[#FAF8F5]">
                  <div>
                    <p className="font-mono text-sm font-semibold text-stone-800">{o.order_code}</p>
                    <p className="text-xs text-stone-500">{isVendor ? o.from_name : o.to_name} · {o.items.length} item(s)</p>
                  </div>
                  <StatusBadge status={o.status} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-[#E5E0D8] bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold">Partners</h2>
            <Handshake className="h-5 w-5 text-[#0D5C3A]" />
          </div>
          {partners.length === 0 ? (
            <p className="py-8 text-center text-sm text-stone-400">No partners connected.</p>
          ) : (
            <div className="space-y-3">
              {partners.slice(0, 5).map((p) => (
                <div key={p.id} className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-stone-800">{p.name}</p>
                    <p className="font-mono text-xs text-stone-500">{p.profile_code}</p>
                  </div>
                  <span className="text-xs text-stone-400">{p.order_count} orders</span>
                </div>
              ))}
            </div>
          )}
          <button onClick={() => navigate("/app/partners")} className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#0D5C3A] hover:underline" data-testid="dashboard-view-partners">
            Manage partners <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
